'use client'

import { DashboardLayout } from '@/components/dashboard-layout'
import { StatCard } from '@/components/stat-card'
import { Badge } from '@/components/badge'
import { AlertCircle, Activity, Server, Network, TrendingUp } from 'lucide-react'
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts'

// Sample data
const deploymentData = [
  { time: '04:00', value: 120 },
  { time: '08:00', value: 150 },
  { time: '12:00', value: 130 },
  { time: '16:00', value: 200 },
  { time: '20:00', value: 180 },
  { time: '23:59', value: 150 },
]

const systemHealthData = [
  { node: 'X-Node-NY-01', health: 95 },
  { node: 'X-Node-LN-02', health: 88 },
  { node: 'X-Node-SG-03', health: 75 },
]

const nodeStatus = [
  { name: 'X-Node-NY-01', ip: '192.168.1.10', type: 'BARE METAL', cpu: 12, memory: 45, containers: 12, status: 'healthy' },
  { name: 'X-Node-LN-02', ip: '45.32.11.90', type: 'DIGITALOCEAN', cpu: 8, memory: 22, containers: 5, status: 'healthy' },
  { name: 'X-Node-SG-03', ip: '104.1.22.8', type: 'AWS EC2', cpu: 0, memory: 0, containers: 0, status: 'offline' },
]

export default function OverviewPage() {
  return (
    <DashboardLayout
      title="Overview"
      subtitle="Manage honeypots and monitor infrastructure performance"
      actions={
        <div className="flex gap-2">
          <button className="px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:opacity-90 transition-opacity">
            Deploy New Instance
          </button>
        </div>
      }
    >
      {/* Stats Grid */}
      <div className="grid grid-cols-4 gap-4 mb-8">
        <StatCard label="Active Honeypots" value="24" description="+3 in last 24h" icon={Activity} />
        <StatCard label="Total Hosts" value="08" description="All nodes online" icon={Server} />
        <StatCard label="Exposed Ports" value="142" description="Across 8 public IPs" icon={Network} />
        <StatCard label="Raw Log Volume" value="4.2 GB" description="Read-only access enabled" icon={AlertCircle} />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-2 gap-6 mb-8">
        {/* Deployment Activity */}
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-foreground">Deployment Activity</h3>
            <span className="text-sm text-muted-foreground">Past 24 Hours</span>
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={deploymentData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(100, 100, 100, 0.1)" />
              <XAxis dataKey="time" stroke="rgba(200, 200, 200, 0.5)" />
              <YAxis stroke="rgba(200, 200, 200, 0.5)" />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'rgb(30, 30, 30)',
                  border: '1px solid rgb(60, 60, 60)',
                  borderRadius: '8px',
                }}
              />
              <Area type="monotone" dataKey="value" stroke="rgb(100, 150, 255)" fill="rgba(100, 150, 255, 0.1)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* System Health Matrix */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-foreground mb-6">System Health Matrix</h3>
          <div className="space-y-4">
            {systemHealthData.map((item, idx) => (
              <div key={idx} className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-foreground">{item.node}</span>
                  <span className="text-emerald-400 font-medium">HEALTHY</span>
                </div>
                <div className="w-full h-2 bg-secondary rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-full"
                    style={{ width: `${item.health}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-3 gap-4 mt-8 pt-6 border-t border-border">
            <div className="text-center">
              <p className="text-2xl font-bold text-foreground">12.4%</p>
              <p className="text-xs text-muted-foreground mt-1">CPU LOAD</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-foreground">2.1k/s</p>
              <p className="text-xs text-muted-foreground mt-1">DISK OPS</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-foreground">12Mb/s</p>
              <p className="text-xs text-muted-foreground mt-1">NET FLOW</p>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-6">Recent Orchestration Logs</h3>
        <div className="space-y-4">
          <div className="flex items-start gap-4 pb-4 border-b border-border">
            <Badge variant="info" className="mt-1">INFO</Badge>
            <div>
              <p className="text-sm text-foreground">Container 'cowrie-ssh-01' successfully deployed to Node-LN-02 via Docker Engine.</p>
              <p className="text-xs text-muted-foreground mt-1">[2023-10-27 14:22:01]</p>
            </div>
          </div>
          <div className="flex items-start gap-4 pb-4 border-b border-border">
            <Badge variant="warning" className="mt-1">WARN</Badge>
            <div>
              <p className="text-sm text-foreground">Network conflict detected on Node-NY-01 port 80. Auto-assigned to 8080.</p>
              <p className="text-xs text-muted-foreground mt-1">[2023-10-27 14:15:45]</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <Badge variant="error" className="mt-1">SYS</Badge>
            <div>
              <p className="text-sm text-foreground">Global kill switch deactivated by administrator 'AD'. Restarting isolated nodes.</p>
              <p className="text-xs text-muted-foreground mt-1">[2023-10-27 13:45:10]</p>
            </div>
          </div>
        </div>
        <button className="mt-6 text-primary hover:underline text-sm font-medium">
          View Audit Log
        </button>
      </div>
    </DashboardLayout>
  )
}
